"use client";

import posthog from "posthog-js";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { ReaderReportType } from "@/lib/reports/report-type";
import { useExploreRoute } from "../_lib/explore-route-context";
import type { BriefType, ExploreBriefCard } from "../types";

export type BriefSurface =
  | "explore_briefs_section"
  | "explore_briefs_compact"
  | "explore_briefs_flat"
  | "explore_briefs_sidebar"
  | "stories_index_briefs"
  | "story_detail_related_briefs"
  // Internal lab/preview pages — filter out of production funnels.
  | "labs_preview";

interface BriefBaseProps {
  edition: ReaderReportType;
  surface: BriefSurface;
  brief_id: string;
  brief_type: BriefType;
  brief_category: string | null;
  brief_report_date: string;
  brief_position_in_list: number | null;
}

const buildBaseProps = ({
  brief,
  edition,
  surface,
  positionInList,
}: {
  brief: ExploreBriefCard;
  edition: ReaderReportType;
  surface: BriefSurface;
  positionInList?: number | null;
}): BriefBaseProps => ({
  edition,
  surface,
  brief_id: brief.briefId,
  brief_type: brief.briefType,
  brief_category: brief.categorySlug,
  brief_report_date: brief.reportDate,
  brief_position_in_list: positionInList ?? null,
});

export type BriefOpenMethod = "click" | "keyboard" | "deep_link";
type OpenMethod = BriefOpenMethod;

export type BriefShareMethod = "copy_link" | "twitter" | "native";

interface PlayAdvanceMeta {
  position: number;
  total: number;
  via: "auto" | "tap" | "keyboard" | "swipe";
}

export interface BriefTrackingParams {
  brief: ExploreBriefCard;
  edition: ReaderReportType;
  surface: BriefSurface;
  positionInList?: number | null;
}

/**
 * Per-brief tracking. Fires typed events into PostHog with a stable base
 * payload so dashboards can slice by brief_id / brief_type / category / edition
 * / surface without arguing about property names.
 */
export function useBriefTracking(params: BriefTrackingParams) {
  const { brief, edition, surface, positionInList } = params;

  const baseProps = useMemo(
    () => buildBaseProps({ brief, edition, surface, positionInList }),
    [brief, edition, surface, positionInList]
  );

  const capture = useCallback(
    (eventName: string, extra?: Record<string, unknown>) => {
      if (typeof window === "undefined") {
        return;
      }
      posthog.capture(eventName, { ...baseProps, ...(extra ?? {}) });
    },
    [baseProps]
  );

  return useMemo(
    () => ({
      trackCardViewed: () => capture("brief_card_viewed"),
      trackCardClicked: () => capture("brief_card_clicked"),
      trackOpened: (openMethod: OpenMethod = "click") =>
        capture("brief_opened", { open_method: openMethod }),
      trackShared: (method: BriefShareMethod) =>
        capture("brief_shared", { method }),
    }),
    [capture]
  );
}

/**
 * Drop-in helper for individual brief row/card components. Reads `edition`
 * from the Explore route context so callers do not have to thread it through
 * every layer, and wires up an IntersectionObserver that fires
 * `brief_card_viewed` exactly once when the row crosses the 50% visibility
 * threshold. Returns a callback ref to attach to the rendered element plus a
 * `handleClick` that captures `brief_card_clicked` before delegating to the
 * caller-supplied click handler.
 */
export function useBriefRowTracking({
  brief,
  surface,
  positionInList,
  onClick,
}: {
  brief: ExploreBriefCard;
  surface: BriefSurface;
  positionInList?: number | null;
  onClick?: () => void;
}) {
  const { reportType: edition } = useExploreRoute();
  const { trackCardViewed, trackCardClicked } = useBriefTracking({
    brief,
    edition,
    surface,
    positionInList,
  });

  const elementRef = useRef<HTMLElement | null>(null);
  const observedRef = useRef(false);

  const setRef = useCallback((node: HTMLElement | null) => {
    elementRef.current = node;
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || observedRef.current) {
      return;
    }
    const node = elementRef.current;
    if (!node) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Verify the actual ratio, not just isIntersecting — IO callbacks
          // can fire on initial observation while < 50 % visible, which
          // would over-count partially visible cards as viewed.
          if (
            entry.isIntersecting &&
            entry.intersectionRatio >= 0.5 &&
            !observedRef.current
          ) {
            observedRef.current = true;
            trackCardViewed();
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [trackCardViewed]);

  const handleClick = useCallback(() => {
    trackCardClicked();
    onClick?.();
  }, [trackCardClicked, onClick]);

  return { ref: setRef, handleClick };
}

/**
 * Standalone helper for Play-mode tracking — used by the overlays where a
 * single session spans many briefs. The overlay passes the group-level edition
 * and surface once; each advance/complete/dismiss call scopes to the brief that
 * was on-screen at that moment.
 */
export function usePlayModeTracking({
  edition,
  fromSurface,
}: {
  edition: ReaderReportType;
  fromSurface: BriefSurface;
}) {
  const trackStarted = useCallback(
    ({ totalBriefs }: { totalBriefs: number }) => {
      if (typeof window === "undefined") {
        return;
      }
      posthog.capture("brief_play_mode_started", {
        edition,
        from_surface: fromSurface,
        total_briefs: totalBriefs,
      });
    },
    [edition, fromSurface]
  );

  const trackAdvanced = useCallback(
    ({ brief, meta }: { brief: ExploreBriefCard; meta: PlayAdvanceMeta }) => {
      if (typeof window === "undefined") {
        return;
      }
      posthog.capture("brief_play_mode_advanced", {
        edition,
        from_surface: fromSurface,
        brief_id: brief.briefId,
        brief_type: brief.briefType,
        brief_category: brief.categorySlug,
        brief_report_date: brief.reportDate,
        position: meta.position,
        total: meta.total,
        via: meta.via,
      });
    },
    [edition, fromSurface]
  );

  const trackCompleted = useCallback(
    ({
      total,
      timeOnPlayModeMs,
    }: {
      total: number;
      timeOnPlayModeMs: number;
    }) => {
      if (typeof window === "undefined") {
        return;
      }
      posthog.capture("brief_play_mode_completed", {
        edition,
        from_surface: fromSurface,
        total,
        time_on_play_mode_ms: timeOnPlayModeMs,
      });
    },
    [edition, fromSurface]
  );

  const trackDismissed = useCallback(
    ({
      position,
      total,
      dismissMethod,
      timeOnPlayModeMs,
    }: {
      position: number;
      total: number;
      dismissMethod: "close_button" | "esc" | "backdrop";
      timeOnPlayModeMs: number;
    }) => {
      if (typeof window === "undefined") {
        return;
      }
      posthog.capture("brief_play_mode_dismissed", {
        edition,
        from_surface: fromSurface,
        position,
        total,
        dismiss_method: dismissMethod,
        time_on_play_mode_ms: timeOnPlayModeMs,
      });
    },
    [edition, fromSurface]
  );

  return useMemo(
    () => ({ trackStarted, trackAdvanced, trackCompleted, trackDismissed }),
    [trackStarted, trackAdvanced, trackCompleted, trackDismissed]
  );
}
